import { RapButton } from "@dynata/rex-raptor";
import React, { useCallback, useRef, useState } from "react";
import type { OrganizationData } from "../../../../services/management-backend/client";
import { useUpdateUser } from "../hooks";
import useDeleteUser from "../hooks/useDeleteUser.hook";
import EditUserPopup from "./editUser.widget";
import { toast } from "react-toastify";
import { GENERIC_ERROR } from "../../../../core/utils/baseError";
import type { IUpdateUserRequestModel } from "@lib/types";
import type User from "models/user";
import type Account from "models/account";
import { ToastConfig } from "core/widgets/toastConfig.widget";

type ManageUserRecordProps = {
  user: User;
  organizationAccounts: Account[];
  setOrgUserPopupState: (state: boolean) => void;
  organizationData: OrganizationData;
  isMe: boolean;
};

const ManageUserRecord: React.FC<ManageUserRecordProps> = ({
  user,
  organizationAccounts,
  setOrgUserPopupState,
  organizationData,
  isMe,
}) => {
  const { mutate: updateUserMutation } = useUpdateUser();
  const { mutate: deleteUserMutation } = useDeleteUser();
  const editUserPopupRef = useRef<HTMLDialogElement>(null);
  const [editUserPopupState, setEditUserPopupState] = useState<boolean>(false);

  const onDeleteUserClick = useCallback(async () => {
    deleteUserMutation(user.id, {
      onSuccess: () => {
        toast.success("User deleted successfully", ToastConfig);
      },
      onError: () => {
        toast.error(GENERIC_ERROR, ToastConfig);
      },
    });
    editUserPopupRef.current?.close();
    setEditUserPopupState(false);
    setOrgUserPopupState(false);
  }, [deleteUserMutation, user.id]);

  const onUpdateUserClick = useCallback(
    async (body: IUpdateUserRequestModel) => {
      updateUserMutation(
        { userId: user.id, body },
        {
          onSuccess: () => {
            toast.success("User updated successfully", ToastConfig);
          },
          onError: () => {
            toast.error(GENERIC_ERROR, ToastConfig);
          },
        },
      );
      editUserPopupRef.current?.close();
      setEditUserPopupState(false);
      setOrgUserPopupState(false);
    },
    [updateUserMutation, user.id],
  );

  const onOpenEditUserPopup = useCallback(() => {
    editUserPopupRef.current?.showModal();
    setEditUserPopupState(true);
    setOrgUserPopupState(true);
  }, []);

  const onCloseEditUserPopup = useCallback(() => {
    editUserPopupRef.current?.close();
    setEditUserPopupState(false);
    setOrgUserPopupState(false);
  }, []);

  return (
    <>
      {editUserPopupState && (
        <div className="manageUser_popup_wrapper">
          <EditUserPopup
            ref={editUserPopupRef}
            user={user}
            onDeleteUserClick={onDeleteUserClick}
            onClose={onCloseEditUserPopup}
            onUpdateUserClick={onUpdateUserClick}
            organizationAccounts={organizationAccounts}
            organizationData={organizationData}
          />
        </div>
      )}
      <tr key={user.id}>
        <td>
          {`${user.firstName} ${user.lastName}`}
          {/*HTML Character Entities for non-breaking space*/}
          {isMe && <strong>&nbsp;(me)</strong>}
        </td>
        <td>{user.username}</td>
        <td>
          {user.isAdmin ? (
            <React.Fragment>
              <span className="role_tag">Organization Owner</span>
            </React.Fragment>
          ) : organizationAccounts.length > 1 ? (
            user.roles.map((role) => (
              <span
                className="role_tag"
                key={`${user.cognitoUsername}-role-${role.role}`}
              >
                <b>{role.accountName}</b> ({role.role})
              </span>
            ))
          ) : organizationAccounts.length === 1 ? (
            <React.Fragment>
              <span className="role_tag">{user.roles.at(0)?.role}</span>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <span className="no_account_tag">No accounts</span>
            </React.Fragment>
          )}
        </td>
        <td>
          {isMe || (
            <RapButton
              styleType="link"
              size="small"
              color="main"
              onClick={onOpenEditUserPopup}
            >
              Edit
            </RapButton>
          )}
        </td>
      </tr>
    </>
  );
};

export default ManageUserRecord;
